<template>
  <div class="salary-import">
    <!-- 1. 导入历史记录 (主页面) -->
    <a-card title="工资导入记录" :bordered="false" class="history-card">
      <div slot="extra" class="history-actions">
        <a-input-search
          placeholder="搜索导入ID、文件名..."
          style="width: 250px"
          v-model="query.keyword"
          @search="fetchHistory"
        />
        <a-button icon="reload" @click="fetchHistory" style="margin-left: 10px;" />
        <a-button type="primary" icon="upload" @click="showImportModal = true" style="margin-left: 10px;">导入工资数据</a-button>
      </div>
      <a-table
        :columns="historyColumns"
        :data-source="historyData"
        :loading="loadingHistory"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
      >
        <template slot="importType" slot-scope="text">
          <a-tag :color="text === 1 ? 'blue' : 'green'">{{ text === 1 ? '教职工' : '工勤' }}</a-tag>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '有效' : '无效' }}</a-tag>
        </template>
        <template slot="action" slot-scope="record">
          <a @click="goDetail(record)">详情</a>
          <a-divider type="vertical" />
          <a-popconfirm
            :title="record.status === 1 ? '确定要设为无效吗？' : '确定要设为有效吗？'"
            @confirm="toggleStatus(record)"
          >
            <a :style="{ color: record.status === 1 ? '#ff4d4f' : '#52c41a' }">
              {{ record.status === 1 ? '设为无效' : '设为有效' }}
            </a>
          </a-popconfirm>
        </template>
      </a-table>
    </a-card>

    <!-- 2. 导入弹窗 -->
    <a-modal
      v-model="showImportModal"
      title="导入工资数据"
      :width="700"
      :footer="null"
      :maskClosable="false"
      @cancel="resetImport"
    >
      <div v-if="!parsing && !importResult">
        <div class="import-tips">请选择导入类型并上传对应的工资Excel文件，支持 .xlsx, .xls 格式。</div>
        <a-form-model :model="importForm" layout="vertical" class="import-form">
          <a-form-model-item label="选择导入类型">
            <a-radio-group v-model="importForm.type" button-style="solid">
              <a-radio :value="1">教职工工资数据</a-radio>
              <a-radio :value="2">工勤人员工资数据</a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-form-model-item label="上传文件">
            <a-upload-dragger
              name="file"
              :multiple="false"
              :file-list="fileList"
              :before-upload="beforeUpload"
              @change="handleFileChange"
              accept=".xlsx, .xls"
            >
              <p class="ant-upload-drag-icon">
                <a-icon type="cloud-upload" />
              </p>
              <p class="ant-upload-text">点击或拖拽文件至此处上传</p>
              <p class="ant-upload-hint">支持格式：.xlsx, .xls（单个文件不超过10MB）</p>
              <a-button type="primary" size="small" style="margin-top: 8px;">选择Excel文件</a-button>
            </a-upload-dragger>
          </a-form-model-item>
          <div class="template-links">
            如需规范格式，可下载模板：
            <a href="javascript:;" @click="downloadTemplate(1)"><a-icon type="download" /> 教职工工资模板.xlsx</a>
            <a href="javascript:;" @click="downloadTemplate(2)" style="margin-left: 20px;"><a-icon type="download" /> 工勤人员工资模板.xlsx</a>
          </div>
          <div class="modal-actions" style="margin-top: 32px; text-align: right;">
            <a-button @click="showImportModal = false">取消</a-button>
            <a-button type="primary" @click="startImport" :loading="importing" style="margin-left: 10px;">开始导入</a-button>
          </div>
        </a-form-model>
      </div>

      <!-- 解析进度 (改为通用加载) -->
      <div v-else-if="parsing" class="parsing-container" style="text-align: center; padding: 40px 0;">
        <a-spin size="large" tip="数据处理中..." />
        <div class="parsing-status" style="margin-top: 20px;">
          系统正在处理工资数据，请耐心等待...
        </div>
      </div>

      <!-- 导入结果 -->
      <div v-else-if="importResult" class="result-container">
        <a-result
          :status="importResult.failCount > 0 ? 'warning' : 'success'"
          :title="importResult.failCount > 0 ? '导入部分成功' : '导入成功'"
        >
          <template slot="subTitle">
            <div class="result-stats">
              <a-row :gutter="16">
                <a-col :span="8">
                  <div class="info-item">
                    <div class="label">总记录数</div>
                    <div class="value">{{ importResult.totalCount }}条</div>
                  </div>
                </a-col>
                <a-col :span="8">
                  <div class="info-item">
                    <div class="label">成功导入</div>
                    <div class="value success">{{ importResult.successCount }}条</div>
                  </div>
                </a-col>
                <a-col :span="8">
                  <div class="info-item">
                    <div class="label">导入失败</div>
                    <div class="value error">{{ importResult.failCount }}条</div>
                  </div>
                </a-col>
              </a-row>
            </div>
          </template>
        </a-result>

        <div v-if="importResult.failCount > 0" class="error-detail">
          <div class="error-header">失败详情({{ importResult.failCount }}条)</div>
          <a-table 
            :columns="errorColumns" 
            :data-source="importResult.errorDetails" 
            size="small" 
            :pagination="{ pageSize: 5 }"
            row-key="rowNum"
          >
            <template slot="reason" slot-scope="text">
              <span class="error-text" style="color: #f5222d;">{{ text }}</span>
            </template>
          </a-table>
        </div>
        <div class="result-actions" style="margin-top: 24px; text-align: center;">
          <a-button type="primary" @click="closeResult">确定</a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { getSalaryHistory, setSalaryStatus, importSalary } from '@/services/salary'
import config from '@/pathConfig'

export default {
  name: 'SalaryImport',
  data() {
    return {
      showImportModal: false,
      importForm: {
        type: 1
      },
      fileList: [],
      importing: false,
      parsing: false,
      importResult: null,
      loadingHistory: false,
      historyData: [],
      query: {
        keyword: ''
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      errorColumns: [
        { title: '行号', dataIndex: 'rowNum', width: 60 },
        { title: '姓名', dataIndex: 'name', width: 100 },
        { title: '身份证号', dataIndex: 'idCard', width: 160 },
        { title: '部门', dataIndex: 'department', width: 120 },
        { title: '失败原因', dataIndex: 'errorReason', scopedSlots: { customRender: 'reason' } }
      ],
      historyColumns: [
        { title: '导入ID', dataIndex: 'id' },
        { title: '导入类型', dataIndex: 'importType', scopedSlots: { customRender: 'importType' } },
        { title: '导入文件', dataIndex: 'fileName', width: 200 },
        { title: '导入人', dataIndex: 'operatorName' },
        { title: '导入时间', dataIndex: 'importTime' },
        { title: '总数', dataIndex: 'totalCount' },
        { title: '成功', dataIndex: 'successCount', customRender: (val) => <span style="color: #52c41a; font-weight: 500;">{val || 0}</span> },
        { title: '失败', dataIndex: 'failCount', customRender: (val) => <span style="color: #f5222d; font-weight: 500;">{val || 0}</span> },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  mounted() {
   
    this.fetchHistory()
  },
  methods: {
    beforeUpload(file) {
      this.fileList = [file]
      return false
    },
    handleFileChange({ fileList }) {
      this.fileList = fileList.slice(-1)
    },
    resetImport() {
      this.fileList = []
      this.importForm.type = 1
      this.parsing = false
      this.importResult = null
      this.parsePercent = 0
    },
    startImport() {
      if (this.fileList.length === 0) {
        this.$message.warning('请选择要上传的文件')
        return
      }
      this.importing = true
      this.parsing = true
      
      const formData = new FormData()
      formData.append('file', this.fileList[0].originFileObj || this.fileList[0])
      formData.append('type', this.importForm.type)

      importSalary(formData).then(res => {
        const result = res.data.data || res.data
        this.importResult = result
        this.fetchHistory()
      }).catch(err => {
        this.$message.error('上传失败')
      }).finally(() => {
        this.importing = false
        this.parsing = false
      })
    },
    closeResult() {
      this.showImportModal = false
      this.resetImport()
    },
    downloadTemplate(type) {
      const name = type === 1 ? '教职工工资模板.xls' : '工勤人员工资模板.xls'
      const url = `${config.baseUrl}salaryPath/${name}`
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.download = name
      link.click()
    },
    fetchHistory() {
      this.loadingHistory = true
      getSalaryHistory({
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        keyword: this.query.keyword
      }).then(res => {
        const result = res.data
        this.historyData = result.list || []
        this.pagination.total = result.total || 0
        this.loadingHistory = false
      }).catch(() => {
        this.loadingHistory = false
      }).finally(() => {
        this.loadingHistory = false
      })
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.fetchHistory()
    },
    goDetail(record) {
      this.$router.push({
        path: '/salary/gongzi/importDetail',
        query: { id: record.id, fileName: record.fileName }
      })
    },
    toggleStatus(record) {
      const newStatus = record.status === 1 ? 0 : 1
      setSalaryStatus({ 
        id: record.id, 
        status: newStatus,
        type: 'record' 
      }).then(() => {
        this.$message.success('状态更新成功')
        this.fetchHistory()
      })
    }
  }
}
</script>

<style lang="less" scoped>
.salary-import {
  padding: 24px;
  background-color: #f0f2f5;
  min-height: 100vh;

  .history-actions {
    display: flex;
    align-items: center;
  }

  .import-tips {
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 20px;
  }

  .template-links {
    margin-top: 16px;
    a {
      color: #1890ff;
    }
  }

  .parsing-container {
    padding: 20px 0;
    .parsing-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
      .title { font-weight: bold; }
      .percent { color: #1890ff; }
    }
    .parsing-status {
      margin-top: 12px;
      color: rgba(0, 0, 0, 0.45);
      text-align: center;
    }
  }

  .error-header {
    font-weight: 500;
    margin: 16px 0 8px;
    color: #f5222d;
  }

  .result-stats {
    margin-top: 16px;
    padding: 16px;
    background: #fafafa;
    border-radius: 4px;
    text-align: left;
  }

  .info-item {
    text-align: center;
    .label {
      color: rgba(0, 0, 0, 0.45);
      margin-bottom: 4px;
      font-size: 14px;
    }
    .value {
      font-size: 18px;
      color: rgba(0, 0, 0, 0.85);
      font-weight: 500;

      &.success { color: #52c41a; }
      &.error { color: #f5222d; }
    }
  }
}
</style>
